import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { TanstackSortingFn } from "../../../utils/TableSorting";
import SortIcon from "../../../utils/TableSortingIcons";
import Pagination from "../Pagination/Pagination";
import { CustomLoadingOverlay } from "../CustomLoadingOverlay/CustomLoadingOverLay";

export default function BaseTable({
  data = [],
  columns = [],
  pageSize = 10,
  paginationMode = "client", // "client" | "server"
  pagination: controlledPagination,
  onPaginationChange,
  totalElements = 0,
  totalPages = 0,
  emptyMessage = "No records found",
  showPagination = true,
  showPaginationNavigation = true,
  className = "",
  rowClassName,
  onRowClick,
  onTableInstance,
  isLoading = false,
  isFetching = false,
  columnVisibility,
}) {
  const [sorting, setSorting] = React.useState([]);

  const [localPagination, setLocalPagination] = React.useState({
    pageIndex: 0,
    pageSize,
  });
  const pagination = controlledPagination || localPagination;

  const handlePaginationChange = (newPagination) => {
    const oldPageIndex = Number(pagination?.pageIndex ?? 0);
    const oldPageSize = Number(pagination?.pageSize ?? pageSize);
    const newPageSize = Number(newPagination?.pageSize ?? oldPageSize);

    if (oldPageSize !== newPageSize) {
      const firstRowIndex = oldPageIndex * oldPageSize;
      const newPageIndex = Math.floor(firstRowIndex / newPageSize);

      (controlledPagination ? onPaginationChange : setLocalPagination)({
        ...newPagination,
        pageIndex: newPageIndex,
      });
    } else {
      (controlledPagination ? onPaginationChange : setLocalPagination)(
        newPagination
      );
    }
  };

  const pageCount =
    paginationMode === "server"
      ? totalPages ||
        Math.ceil((totalElements || 0) / (pagination.pageSize || pageSize))
      : undefined;

  const table = useReactTable({
    data,
    columns,
    state: { sorting, pagination, ...(columnVisibility ? { columnVisibility } : {}) },
    onSortingChange: setSorting,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(paginationMode === "client"
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
    ...(paginationMode === "server"
      ? { manualPagination: true, pageCount }
      : {}),
    defaultColumn: {
      sortingFn: TanstackSortingFn,
    },
  });

  React.useEffect(() => {
    if (onTableInstance) onTableInstance(table);
  }, [table, onTableInstance]);

  return (
    <div className={`flex flex-col ${className} overflow-x-auto `}>
      <div className="min-w-[max-content] flex flex-col">
      <div className="rounded bg-white-50 relative">
        <table className="min-w-full divide-y divide-neutral-800 ">
          <thead className="bg-neutral-900 top-0 ">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs sm:text-sm font-semibold text-[#4B5563] cursor-pointer select-none"
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 block truncate">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </span>
                      {header.column.getCanSort() && (
                        <SortIcon sort={header.column.getIsSorted()} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className="divide-y divide-neutral-800">
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={
                    rowClassName
                      ? rowClassName(row)
                      : "hover:bg-primary-50 cursor-pointer bg-white-50"
                  }
                  onClick={() => onRowClick && onRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm text-black-50 text-left"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-8 text-neutral-400"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {(isFetching || (isLoading && data.length > 0)) && (
          <div className="absolute inset-0 left-0 right-0 z-50">
            <CustomLoadingOverlay
              visible={true}
              overlayProps={{ radius: "md", blur: 2 }}
              loaderProps={{ color: "primary", type: "bars" }}
            />
          </div>
        )}
      </div>

      {showPagination && (
        <div className="border-neutral-200 bg-white-50 p-2 sm:p-4 mt-4">
          <Pagination
            mode={paginationMode}
            pagination={pagination}
            setPagination={handlePaginationChange}
            totalElements={totalElements}
            totalPages={totalPages}
            disabled={isLoading || isFetching}
            showNavigation={showPaginationNavigation}
            table={paginationMode === "client" ? table : undefined}
          />
        </div>
      )}
      </div>
    </div>
  );
}
